import { cn } from '@/constants/design-tokens';
import type { BillStatus } from '@/features/bills';

export interface BillStatusMeta {
  label: string;
  /** Gradient stops for the hero banner on the detail page. */
  gradient: string;
  /** Pill tint + text colors — shared by both the detail and list pages. */
  pillBg: string;
  pillText: string;
  /** One-line description shown on the detail page hero. */
  description: string;
}

export const BILL_STATUS_META: Record<BillStatus, BillStatusMeta> = {
  COMPLETED: {
    label: 'Completed',
    gradient: 'from-success-600 via-success-500 to-emerald-600',
    pillBg: 'bg-success-100 dark:bg-success-900/40',
    pillText: 'text-success-700 dark:text-success-200',
    description: 'Bill closed and payment captured.',
  },
  DRAFT: {
    label: 'Draft',
    gradient: 'from-neutral-500 via-neutral-400 to-neutral-500',
    pillBg: 'bg-neutral-200 dark:bg-neutral-700',
    pillText: 'text-neutral-800 dark:text-neutral-100',
    description: 'Draft — being rung up',
  },
  PARKED: {
    label: 'Parked',
    gradient: 'from-indigo-600 via-indigo-500 to-violet-600',
    pillBg: 'bg-indigo-100 dark:bg-indigo-900/40',
    pillText: 'text-indigo-700 dark:text-indigo-200',
    description: 'Parked — waiting at the till',
  },
  RETURNED: {
    label: 'Returned',
    gradient: 'from-warning-600 via-warning-500 to-warning-600',
    pillBg: 'bg-warning-100 dark:bg-warning-900/40',
    pillText: 'text-warning-700 dark:text-warning-200',
    description: 'A credit note was issued against this bill.',
  },
  VOID: {
    label: 'Void',
    gradient: 'from-danger-600 via-danger-500 to-danger-600',
    pillBg: 'bg-danger-100 dark:bg-danger-900/40',
    pillText: 'text-danger-700 dark:text-danger-200',
    description: 'This bill was cancelled and no payment was captured.',
  },
  PENDING_DISCOUNT_APPROVAL: {
    label: 'Pending approval',
    gradient: 'from-info-600 via-info-500 to-info-600',
    pillBg: 'bg-info-100 dark:bg-info-900/40',
    pillText: 'text-info-700 dark:text-info-200',
    description: 'Awaiting a manager approval before completion.',
  },
};

/** Neutral look for a status this build doesn't know, so a new one never blanks the page. */
const UNKNOWN_STATUS_META: Omit<BillStatusMeta, 'label'> = {
  gradient: 'from-neutral-500 via-neutral-400 to-neutral-500',
  pillBg: 'bg-neutral-100 dark:bg-neutral-700/60',
  pillText: 'text-neutral-700 dark:text-neutral-200',
  description: '',
};

export function billStatusMeta(status: string): BillStatusMeta {
  return BILL_STATUS_META[status as BillStatus] ?? { ...UNKNOWN_STATUS_META, label: status };
}

export function BillStatusPill({ status }: { status: string }) {
  const m = billStatusMeta(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold',
        m.pillBg,
        m.pillText,
      )}
    >
      {m.label}
    </span>
  );
}
